import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/base/VideoCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteConfig } from '@/mocks/siteConfig';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

const heroBgImage = 'https://readdy.ai/api/search-image?query=Cinematic%20abstract%20background%20with%20warm%20red%20and%20gold%20gradient%20overlay%2C%20film%20texture%2C%20soft%20bokeh%20lights%2C%20elegant%20dark%20cinema%20atmosphere%2C%20subtle%20geometric%20patterns%2C%20professional%20streaming%20platform%20hero%20visual%2C%20no%20text%2C%20no%20people&width=1800&height=1000&seq=hero-home-bg&orientation=landscape';

const categoryImages: Record<string, string> = {
  dianying: 'https://readdy.ai/api/search-image?query=Cinematic%20movie%20theater%20with%20projector%20beam%2C%20warm%20red%20velvet%20curtains%2C%20dramatic%20lighting%2C%20clean%20elegant%20atmosphere%2C%20professional%20cinema%20photography%20style%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=cat-dianying-hero&orientation=landscape',
  dianshiju: 'https://readdy.ai/api/search-image?query=Modern%20living%20room%20with%20large%20TV%20screen%2C%20cozy%20warm%20lighting%2C%20comfortable%20atmosphere%2C%20clean%20interior%20design%2C%20professional%20home%20entertainment%20photography%20style%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=cat-dianshiju-hero&orientation=landscape',
  zongyi: 'https://readdy.ai/api/search-image?query=Colorful%20TV%20studio%20stage%20with%20bright%20lights%20and%20confetti%2C%20cheerful%20vibrant%20atmosphere%2C%20clean%20background%2C%20professional%20variety%20show%20photography%20style%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=cat-zongyi-hero&orientation=landscape',
  dongman: 'https://readdy.ai/api/search-image?query=Colorful%20anime%20art%20studio%20with%20drawing%20tablet%20and%20sketches%2C%20warm%20creative%20atmosphere%2C%20clean%20workspace%2C%20professional%20animation%20studio%20photography%20style%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=cat-dongman-hero&orientation=landscape',
  jilupian: 'https://readdy.ai/api/search-image?query=Professional%20camera%20equipment%20in%20nature%20landscape%2C%20golden%20hour%20lighting%2C%20majestic%20mountains%20background%2C%20clean%20atmosphere%2C%20documentary%20photography%20style%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=cat-jilupian-hero&orientation=landscape',
  shaoer: 'https://readdy.ai/api/search-image?query=Colorful%20children%20playroom%20with%20toys%20and%20books%2C%20bright%20cheerful%20lighting%2C%20warm%20atmosphere%2C%20clean%20background%2C%20professional%20kids%20space%20photography%20style%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=cat-shaoer-hero&orientation=landscape',
};

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const hotVideos = videos.filter((v) => v.isHot).slice(0, 8);
  const newVideos = videos.filter((v) => v.isNew && !v.isHot).slice(0, 8);
  const featuredArticles = articles.filter((a) => a.isFeatured).slice(0, 4);
  const heroVideos = videos.filter((v) => v.isHot).slice(0, 5);

  const faqs = [
    {
      q: '苹果视频是否需要付费？',
      a: '苹果视频是一个完全免费的影视平台，所有电影、电视剧、综艺、动漫、纪录片资源均可免费在线观看，无需付费，无需会员，免费看个够。',
    },
    {
      q: '观看视频需要下载APP吗？',
      a: '不需要。苹果视频支持网页端直接在线播放，无需下载任何软件或APP。当然，我们也提供APP版本供手机用户获得更好的观影体验，但这是完全可选的。',
    },
    {
      q: '视频播放会卡顿缓冲吗？',
      a: '苹果视频采用了先进的视频压缩和CDN加速技术，确保在线播放流畅不缓冲。当然，播放质量也会受到您的网络环境影响，建议在Wi-Fi环境下观看4K高清内容。',
    },
    {
      q: '支持哪些设备观看？',
      a: '苹果视频支持所有主流设备，包括电脑（Windows/Mac）、手机（iOS/Android）、平板、智能电视等。只要您的设备有浏览器，就能访问苹果视频在线观看。',
    },
    {
      q: '视频更新频率如何？',
      a: '我们每天都会更新最新的电影和电视剧资源。热门剧集和综艺节目通常在播出后24小时内上线，确保您不会错过任何精彩内容。',
    },
    {
      q: '可以下载视频离线观看吗？',
      a: '目前苹果视频主要提供在线播放服务。下载功能的体验正在优化中，敬请期待后续更新。',
    },
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteConfig.name,
        url: siteConfig.url,
        description: siteConfig.description,
        email: siteConfig.email,
        telephone: siteConfig.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '望京科技园A座18层',
          addressLocality: '北京',
          addressRegion: '朝阳区',
          addressCountry: 'CN',
        },
        foundingDate: siteConfig.founded,
      },
      {
        '@type': 'WebSite',
        name: siteConfig.name,
        url: siteConfig.url,
        description: siteConfig.description,
        inLanguage: 'zh-CN',
        potentialAction: {
          '@type': 'SearchAction',
          'target': `${siteConfig.url}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${siteConfig.url}/#webpage`,
        url: siteConfig.url,
        name: `${siteConfig.name} - 免费观看高清电影电视剧全集`,
        description: siteConfig.description,
        inLanguage: 'zh-CN',
        isPartOf: { '@id': `${siteConfig.url}/#website` },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a,
          },
        })),
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src={heroBgImage}
            alt="苹果视频 - 免费在线观看高清影视"
            className="absolute inset-0 w-full h-full object-cover"
            title="苹果视频 - 免费高清影视平台"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 md:px-6">
            <div className="w-full max-w-3xl text-center">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 leading-tight">
                免费观看高清电影电视剧全集
              </h1>
              <p className="text-base md:text-lg text-background-200 mb-8 max-w-xl mx-auto leading-relaxed">
                海量影视资源免费在线播放，流畅不缓冲，每日更新热门电影、电视剧、综艺、动漫、纪录片。苹果视频，免费看个够！
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/dianying"
                  className="px-8 py-3 text-sm font-medium bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  立即观看 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 text-sm font-medium bg-background-50/10 text-background-50 backdrop-blur-sm border border-background-50/20 rounded-full hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>

          {/* Hero Carousel Dots */}
          <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2">
            {heroVideos.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  idx === currentSlide
                    ? 'bg-primary-500 w-6'
                    : 'bg-background-50/40 hover:bg-background-50/60'
                }`}
                aria-label={`切换到第${idx + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                视频分类
              </h2>
              <Link
                to="/category/dianying"
                className="text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line ml-1 text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group relative overflow-hidden rounded-lg aspect-[3/2] cursor-pointer"
                >
                  <img
                    src={categoryImages[cat.id] || categoryImages.dianying}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    title={cat.name}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-background-50 text-sm`}></i>
                      </div>
                      <span className="text-sm font-heading font-semibold text-background-50">
                        {cat.name}
                      </span>
                    </div>
                    <span className="text-xs text-background-300">{cat.count}部资源</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Videos */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  热门推荐
                </h2>
              </div>
              <Link
                to="/category/dianying"
                className="text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                更多热门 <i className="ri-arrow-right-line ml-1 text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {hotVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </div>
        </section>

        {/* New Videos */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  最新上线
                </h2>
              </div>
              <Link
                to="/category/dianying"
                className="text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                更多新片 <i className="ri-arrow-right-line ml-1 text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {newVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </div>
        </section>

        {/* All Categories Quick View */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                按分类浏览
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                六大视频分类，涵盖电影、电视剧、综艺、动漫、纪录片、少儿等内容，总有一类适合你。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-colors group cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-heading font-semibold text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                      {cat.description}
                    </p>
                    <span className="inline-block mt-2 text-[11px] text-foreground-400">
                      {cat.count}+ 部资源
                    </span>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-secondary-500 rounded-full"></div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  影视资讯
                </h2>
              </div>
              <Link
                to="/category/dianying"
                className="text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                更多资讯 <i className="ri-arrow-right-line ml-1 text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group block bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      title={article.title}
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] text-foreground-400">{article.date}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-foreground-300"></span>
                      <span className="text-[10px] text-foreground-400">{article.views}次阅读</span>
                    </div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* App Download CTA */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-foreground-950">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-primary-500 rounded-2xl">
              <i className="ri-smartphone-line text-background-50 text-2xl"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              随时随地，想看就看
            </h2>
            <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-md mx-auto leading-relaxed">
              下载苹果视频APP，享受更流畅的观影体验。支持iOS和Android，离线缓存功能即将上线。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/about"
                className="px-8 py-3 text-sm font-medium bg-background-50 text-foreground-950 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-apple-fill mr-2"></i>App Store
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 text-sm font-medium bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-android-fill mr-2"></i>Google Play
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">
                关于苹果视频的一些常见疑问，在这里找到答案
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    aria-expanded={faqOpen === idx}
                  >
                    <span className="text-sm font-medium text-foreground-800 pr-4">
                      {faq.q}
                    </span>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i
                        className={`text-foreground-400 transition-transform duration-300 ${
                          faqOpen === idx ? 'ri-subtract-line' : 'ri-add-line'
                        }`}
                      ></i>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      faqOpen === idx ? 'max-h-60' : 'max-h-0'
                    }`}
                  >
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-mail-send-line text-secondary-600 text-xl"></i>
            </div>
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-2">
              订阅更新通知
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              第一时间获取最新影片上线通知和影视资讯
            </p>
            <form
              data-readdy-form
              action="https://readdy.ai/api/form/d9n1atobdkpk99j7gfeg"
              method="POST"
              className="flex flex-col sm:flex-row gap-3"
            >
              <div className="honeypot-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>
              <input
                type="email"
                name="email"
                placeholder="输入您的邮箱地址"
                required
                className="flex-1 px-4 py-3 text-sm bg-background-100 border border-background-200/70 rounded-full text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-all"
              />
              <button
                type="submit"
                className="px-6 py-3 text-sm font-medium bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即订阅
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}